import {
  CyclabilityZone,
  CyclabilityZoneService,
  ParkingService,
  Place,
  PoiService,
  TPoiCategoryCode,
  useCancellablePromise,
  useCyclabilityZones,
  useFacilities,
  useGeoveloMap,
  useParkingLots,
  usePois,
  useReports,
  useUnits,
} from '@geovelo-frontends/commons';
import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { GeoJSONSource, LngLatBounds, MapLayerMouseEvent, Popup } from 'maplibre-gl';
import { useSnackbar } from 'notistack';
import {
  ReactNode,
  Ref,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import { AppContext } from '../../app/context';
import { LegendControl } from '../../components/legend-control';
import SearchControl from '../../components/search-control';
import { environment } from '../../environment';
import { cacheZonesStats, getDefaultStatsPeriod, withStats } from '../../utils/zone-stats';

const mapId = 'map';

const usefulCategories: TPoiCategoryCode[] = [
  'useful',
  'bicycleRental',
  'bicyclePump',
  'bicycleHome',
  'drinkingWater',
  'toilets',
  'picnic',
  'tourismOffice',
  'chargingStation',
];

export type TMapRef = { resize: () => void };

function Map(
  { children, ...props }: { children: ReactNode } & BoxProps,
  ref: Ref<TMapRef>,
): React.JSX.Element {
  const [fitBoundsPrevented, preventFitBounds] = useState(false);
  const {
    zone: { map: zoneMap, codeMap: zoneCodeMap, childrenMap, current: currentZone },
    stats: { byZone, selectedKey: selectedPeriodKey },
    pois: { categories: poiCategories },
    counts: { displayMode: countingDisplayMode, selectedSiteId, sites: countingSites },
    report: { reports, selectedReport },
    routing: { currentTab },
    actions: { setMap, setSelectedCountingSiteId, setZoneMap, setZoneCodeMap },
  } = useContext(AppContext);
  const searchControlRef = useRef(new SearchControl('search-control'));
  const legendControlRef = useRef(new LegendControl('legend-control'));
  const countingPopupRef = useRef<Popup | null>(null);
  const countingSitesRef = useRef(countingSites);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toDistance } = useUnits();
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    map,
    initialized: mapInitialized,
    bounds,
    zoom,
    init: initMap,
  } = useGeoveloMap({ baseLayersPlacement: 'top-right' });
  const { initialized: facilitiesInitialized, init: initFacilities } = useFacilities(map);
  const {
    initialized: cyclabilityZonesInitialized,
    init: initCyclabilityZones,
    update: updateCyclabilityZones,
    clear: clearCyclabilityZones,
  } = useCyclabilityZones(
    map,
    zoneMap,
    {
      onClick: handleCyclabilityZoneClick,
      getTooltipContent: (properties) => {
        return `<h3>${properties?.name}</h3><span>${toDistance(
          (properties?.distance || 0) * 1000,
        )}</span>`;
      },
    },
    {},
  );
  const {
    selectedParking,
    initialized: parkingLotsInitialized,
    init: initParkingLots,
    update: updateParkingLots,
    unselect: unselectParkingLot,
  } = useParkingLots(map, theme);
  const {
    initialized: reportsInitialized,
    init: initReports,
    update: updateReports,
    select: selectReport,
    clear: clearReports,
  } = useReports(map, theme, {
    onDetailsClick: (id) => {
      navigate(
        currentZone?.administrativeLevel === 'world'
          ? `/contributions/${id}`
          : currentZone?.administrativeLevel === 'country'
            ? `/${currentZone.countryCode}/contributions/${id}`
            : currentZone?.administrativeLevel === 'region'
              ? `/${currentZone.countryCode}/${encodeURIComponent(currentZone.name)}/contributions/${id}`
              : `/${currentZone?.countryCode}/${currentZone?.code}/contributions/${id}`,
      );
    },
  });
  const {
    selectedPoi,
    initialized: poisInitialized,
    init: initPois,
    update: updatePois,
    unselect: unselectPoi,
  } = usePois(
    map,
    theme,
    false,
    poiCategories,
    usefulCategories.reduce<{ [key in TPoiCategoryCode]?: boolean }>((res, key) => {
      res[key] = true;

      return res;
    }, {}),
    undefined,
    { enqueueSnackbar, closeSnackbar },
  );
  const {
    cancellablePromise: cancellableParkingLotsPromise,
    cancelPromises: cancelParkingLotsPromise,
  } = useCancellablePromise();
  const { cancellablePromise: cancellablePoisPromise, cancelPromises: cancelPoisPromise } =
    useCancellablePromise();

  useEffect(() => {
    if (!mapInitialized) {
      initMap({
        container: mapId,
        customZoomControls: true,
        scaleControl: true,
        minZoom: 2,
        baseLayersControl: true,
      });
    }
  }, []);

  useEffect(() => {
    if (map) setMap(map);
  }, [map]);

  useEffect(() => {
    countingSitesRef.current = countingSites;
  }, [countingSites]);

  useEffect(() => {
    if (!mapInitialized) return;

    if (map && !map.hasControl(searchControlRef.current)) {
      map.addControl(searchControlRef.current, 'top-left');

      searchControlRef.current.init(theme, { onSelect: handlePlaceSelected });
    }

    if (map && !map.hasControl(legendControlRef.current)) {
      map.addControl(legendControlRef.current, 'bottom-left');

      legendControlRef.current.init(theme);
    }

    if (!facilitiesInitialized) initFacilities(true, environment.config.useIDFMFacilitiesStyle);
    if (!cyclabilityZonesInitialized) initCyclabilityZones();
    if (!parkingLotsInitialized) initParkingLots();
    if (!poisInitialized) initPois();
    if (!reportsInitialized) initReports();
  }, [mapInitialized]);

  useEffect(() => {
    if (!environment.config.enableCounts || !mapInitialized || !map) return;

    initCountingLayer();
  }, [mapInitialized]);

  useEffect(() => {
    if (parkingLotsInitialized) getParkingLots();
  }, [parkingLotsInitialized, bounds, currentTab]);

  useEffect(() => {
    if (poiCategories && poisInitialized) getPois();
  }, [poiCategories, poisInitialized, bounds, currentTab]);

  useEffect(() => {
    if (selectedParking) unselectPoi();
  }, [selectedParking]);

  useEffect(() => {
    if (selectedPoi) unselectParkingLot();
  }, [selectedPoi]);

  useEffect(() => {
    if (reportsInitialized) {
      if (currentTab === 'contributions') {
        if (selectedReport) selectReport(selectedReport);
        else if (reports) updateReports(reports);
        else clearReports();
      } else {
        clearReports();
      }
    }
  }, [reportsInitialized, selectedReport, reports, currentTab]);

  useEffect(() => {
    if (reportsInitialized && currentTab === 'contributions' && !selectedReport && reports)
      updateReports(reports);
  }, [zoom]);

  useEffect(() => {
    if (!environment.config.enableCounts || !mapInitialized || !map) return;

    const source = getCountingSource();
    if (!source) return;

    if (currentTab === 'comptages') source.setData(getCountingFeatureCollection());
    else {
      source.setData({ type: 'FeatureCollection', features: [] });
      countingPopupRef.current?.remove();
      countingPopupRef.current = null;
    }
  }, [countingDisplayMode, countingSites, currentTab, mapInitialized, selectedSiteId]);

  useEffect(() => {
    if (mapInitialized && currentZone) {
      const {
        bounds: { north, east, south, west },
      } = currentZone;

      if (!fitBoundsPrevented) {
        map?.fitBounds(new LngLatBounds({ lat: south, lng: west }, { lat: north, lng: east }), {
          padding: 50,
        });
      }

      preventFitBounds(false);
    }
  }, [mapInitialized, currentZone]);

  useEffect(() => {
    if (!mapInitialized || !currentZone) return;

    if (currentTab === 'stats') {
      if (
        cyclabilityZonesInitialized &&
        childrenMap[currentZone.id] !== undefined &&
        selectedPeriodKey
      ) {
        const childrenIds = childrenMap[currentZone.id];

        updateCyclabilityZones(
          currentZone,
          childrenIds
            ? childrenIds
                .map((id) => zoneMap[id])
                .filter(Boolean)
                .map((child) => withStats(child, byZone[child.id] || []))
            : null,
          selectedPeriodKey,
        );
      }
    }

    return () => {
      if (cyclabilityZonesInitialized) clearCyclabilityZones();
    };
  }, [
    mapInitialized,
    cyclabilityZonesInitialized,
    currentTab,
    currentZone,
    childrenMap,
    selectedPeriodKey,
    byZone,
  ]);

  useImperativeHandle(ref, () => ({
    resize: () => map?.resize(),
  }));

  async function getParkingLots() {
    cancelParkingLotsPromise();

    if (!map || !bounds || currentTab !== 'stats') {
      updateParkingLots([]);
      return;
    }

    try {
      const parkingLots = await cancellableParkingLotsPromise(
        ParkingService.getParkingLots({ zoom: map.getZoom(), bounds }),
      );

      updateParkingLots(parkingLots);
    } catch (err) {
      console.error(err);
    }
  }

  async function getPois() {
    cancelPoisPromise();

    if (!poiCategories || !map || !bounds || currentTab !== 'stats') {
      updatePois([]);
      return;
    }

    try {
      const pois = await cancellablePoisPromise(
        PoiService.getPois(poiCategories, {
          zoom: map.getZoom(),
          bounds,
          selectedCategories: usefulCategories,
        }),
      );

      updatePois(pois);
    } catch (err) {
      console.error(err);
    }
  }

  function initCountingLayer() {
    if (!map) return;

    if (!map.getSource('physical-counts')) {
      map.addSource('physical-counts', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.getLayer('physical-counts-circles')) {
      map.addLayer({
        id: 'physical-counts-circles',
        type: 'circle',
        source: 'physical-counts',
        paint: {
          'circle-color': [
            'case',
            ['==', ['get', 'selected'], true],
            theme.palette.secondary.main,
            ['==', ['get', 'freshness'], 'fresh'],
            theme.palette.success.main,
            ['==', ['get', 'freshness'], 'late'],
            theme.palette.warning.main,
            ['==', ['get', 'freshness'], 'stale'],
            theme.palette.error.main,
            theme.palette.primary.main,
          ],
          'circle-opacity': 0.85,
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'count'],
            0,
            6,
            100,
            9,
            1000,
            16,
            5000,
            24,
          ],
          'circle-stroke-color': '#fff',
          'circle-stroke-width': ['case', ['==', ['get', 'selected'], true], 3, 1.5],
        },
      });

      map.on('click', 'physical-counts-circles', handleCountingFeatureClick);
      map.on('mousemove', 'physical-counts-circles', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'physical-counts-circles', () => {
        map.getCanvas().style.cursor = '';
      });
    }
  }

  function getCountingSource(): GeoJSONSource | null {
    const source = map?.getSource('physical-counts');

    return source instanceof GeoJSONSource ? source : null;
  }

  function getCountingFeatureCollection(): GeoJSON.FeatureCollection<GeoJSON.Point> {
    return {
      type: 'FeatureCollection',
      features: countingSites.flatMap((site) => {
        if (countingDisplayMode === 'sites') {
          return [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: site.coordinates },
              properties: {
                id: site.id,
                type: 'site',
                count: site.count,
                freshness: site.freshness,
                selected: site.id === selectedSiteId,
              },
            },
          ];
        }

        return site.channels.map((channel) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: channel.coordinates },
          properties: {
            id: site.id,
            type: 'channel',
            channelId: channel.id,
            count: channel.count,
            freshness: site.freshness,
            selected: site.id === selectedSiteId,
          },
        }));
      }),
    };
  }

  function handleCountingFeatureClick({ features }: MapLayerMouseEvent) {
    const feature = features?.[0];
    const id = feature?.properties?.id;
    const channelId = feature?.properties?.channelId;
    if (typeof id !== 'string') return;

    setSelectedCountingSiteId(id);
    showCountingPopup(id, typeof channelId === 'string' ? channelId : null);
  }

  function showCountingPopup(siteId: string, channelId: string | null) {
    if (!map) return;

    const site = countingSitesRef.current.find(({ id }) => id === siteId);
    if (!site) return;

    const channel = channelId ? site.channels.find(({ id }) => id === channelId) || null : null;
    const coordinates = channel?.coordinates || site.coordinates;

    countingPopupRef.current?.remove();
    countingPopupRef.current = new Popup({ closeButton: true, maxWidth: '320px' })
      .setLngLat(coordinates)
      .setHTML(getCountingPopupContent(site, channel))
      .addTo(map);
  }

  function getCountingPopupContent(
    site: (typeof countingSites)[number],
    channel: (typeof countingSites)[number]['channels'][number] | null,
  ): string {
    const rows = [
      ['Source', site.sourceName],
      ['Collectivité', site.cityName],
      ['Pas de temps', 'Jour'],
      ['Date', formatCountingDate(channel?.countedAt || site.countedAt)],
      ['Comptage', `${(channel?.count ?? site.count).toLocaleString()} cyclistes`],
      ...(channel?.direction ? [['Sens', channel.direction]] : []),
      ...(channel?.anomalyProbability !== undefined && channel.anomalyProbability !== null
        ? [['Probabilité d’anomalie', String(channel.anomalyProbability)]]
        : []),
    ];

    return `
      <div style="font-family: Roboto, Helvetica, Arial, sans-serif; min-width: 220px;">
        <strong style="display:block; margin-bottom: 6px;">${escapeHtml(
          channel?.name || site.name,
        )}</strong>
        ${rows
          .map(
            ([label, value]) => `
              <div style="display:flex; gap:8px; justify-content:space-between; margin: 3px 0;">
                <span style="color:#666;">${escapeHtml(label)}</span>
                <span style="font-weight:500; text-align:right;">${escapeHtml(value)}</span>
              </div>
            `,
          )
          .join('')}
      </div>
    `;
  }

  function escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatCountingDate(date: string): string {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(new Date(date));
  }

  function handleCyclabilityZoneClick(zone: CyclabilityZone | null) {
    if (zone) {
      const { administrativeLevel, countryCode, code, name } = zone;

      navigate(
        administrativeLevel === 'country'
          ? `/${countryCode}`
          : administrativeLevel === 'region'
            ? `/${countryCode}/${encodeURIComponent(name)}`
            : `/${countryCode}/${code}`,
      );
    }
  }

  async function handlePlaceSelected(place: Place) {
    try {
      const period = getDefaultStatsPeriod();
      const {
        zones: [zone],
      } = await CyclabilityZoneService.getZones({
        administrativeLevel: 'CITY',
        period,
        point: place.point,
      });

      if (zone && zone.id !== currentZone?.id) {
        cacheZonesStats(queryClient, [zone], period);
        if (!zoneMap[zone.id]) setZoneMap({ ...zoneMap, [zone.id]: zone });
        if (!zoneCodeMap[zone.code]) setZoneCodeMap({ ...zoneCodeMap, [zone.code]: zone.id });

        preventFitBounds(true);
        navigate(`/${zone.countryCode}/${zone.code}`);
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Box {...props}>
      <Box height="100%" id={mapId} width="100%" />
      {children}
    </Box>
  );
}

export default forwardRef(Map);
